/* eslint-disable @ngrx/no-dispatch-in-effects */
import { Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Store } from "@ngrx/store";
import { catchError, map, mergeMap, tap } from 'rxjs/operators';
import { concatLatestFrom } from '@ngrx/effects';

import { beginSampleDataLoad, commitStudy, ensureSampleData, loadCalLot, loadCalLotError, loadCalLotSuccess, loadQcLot, loadQcLotError, loadQcLotSuccess, loadSampleData, loadSampleDataFailure, loadSampleDataSuccess, runCalculation, runCalculationError, runCalculationSuccess, runGetCalculationCsv, runGetCalculationCsvSuccess, saveStudy, saveStudyError, saveStudySuccess, setLastCalculationCache, setProcessingStatus, setStudy } from "./actions";
import { selectAppState, selectDiscardWarningSummary } from "./selectors";
import { EMPTY, from, Observable, of } from "rxjs";
import { AppUtilityService } from "../../shared/services/app-utility.service";
import { apiPaths } from "../core/constants/api.const";
import { CalLot } from "../../shared/models/cal-lot.model";
import { QcLot } from "../../shared/models/qc-lot.model";
import { CommonDialogData } from "../../shared/models/dialog.model";
import { CalculationResponse, PullSampleDataResponse, SaveStudyResponse } from "../../shared/models/result.model";
import { CommitRequest, SaveStudyRequest } from "../../shared/models/study.model";
import { normalizeQcLot, saveCsvZipFile } from "../../shared/utils/helper";
import { StudyLogbookService } from "../../shared/services/study-logbook.service";
import { StudyLinksService } from "../../shared/services/study-links.service";


@Injectable()
export class AppEffects {
  appState$ = this.store.select(selectAppState);
  /**
   * 
   * @param actions$ Actions stream
   * @param appUtilityService Service for API calls and utility functions.
   * @param store Injecting store for state management
   */
  constructor(private actions$: Actions,
    private appUtilityService: AppUtilityService,
    private store: Store,
    private studyLogbookService: StudyLogbookService,
    private studyLinksService: StudyLinksService) {
  }

  getCalculationCsvEffect = createEffect(() => {
    return this.actions$.pipe(
      ofType(runGetCalculationCsv),
      concatLatestFrom(() => this.appState$),
      mergeMap(([action, appState]) => {
        const runName = appState.selectedStudy?.studyName?.trim();
        const shouldUseRunName = !!runName && !!appState.selectedStudy?.studyId && !appState.selectedStudy?.isDraft && !appState.hasNewInstrumentUploads;
        const calcCsvUrl = shouldUseRunName
          ? `${this.appUtilityService.getAPI(apiPaths.study.getCalculateCsv)}?run_name=${encodeURIComponent(runName)}`
          : this.appUtilityService.getAPI(apiPaths.study.getCalculateCsv);
        return this.appUtilityService.postAndPollForFile(
          calcCsvUrl,
          action.calculationRequest,
          'calc_get_csv'
        ).pipe(
          map(filePayload => {
            const resolvedFileName = filePayload.filename || 'calculations_response.tar.gz';
            saveCsvZipFile(filePayload.blob, resolvedFileName);
            this.appUtilityService.displayAlertDialog({
              title: "Success",
              message: "Results Zip file Successfully downloaded",
              okText: 'OK',
              dismissText: '',
              okAction: () => { },
              dismissAction: () => { }
            } as CommonDialogData);
            this.store.dispatch(setProcessingStatus({ isProcessing: false }));
            return runGetCalculationCsvSuccess({ calculationResponse: filePayload.blob, fileName: resolvedFileName });
          }),
          catchError(error => {
            this.appUtilityService.displayAlertDialog({
              title: "Calculation Error",
              message: (error as Error)?.message || this.appUtilityService.errorMessages.serviceFailure,
              okText: 'OK',
              dismissText: '',
              okAction: () => { },
              dismissAction: () => { }
            } as CommonDialogData);
            this.store.dispatch(setProcessingStatus({ isProcessing: false }));
            return of(runCalculationError({ error: error }));
          })
        );
      })
    );
  });


  /** Effect to handle running a calculation when the `runCalculation` action is dispatched */
  calculationEffect = createEffect(() => {
    return this.actions$.pipe(
      ofType(runCalculation),
      concatLatestFrom(() => this.appState$),
      mergeMap(([action, appState]) => {
        const runName = appState.selectedStudy?.studyName?.trim();
        const shouldUseRunName = !!runName && !!appState.selectedStudy?.studyId && !appState.selectedStudy?.isDraft && !appState.hasNewInstrumentUploads;
        const calcUrl = shouldUseRunName
          ? `${this.appUtilityService.getAPI(apiPaths.study.calculate)}?run_name=${encodeURIComponent(runName)}`
          : this.appUtilityService.getAPI(apiPaths.study.calculate);
        return this.appUtilityService.postWithPolling<CalculationResponse>(
          calcUrl,
          action.calculationRequest,
          'calc'
        ).pipe(
        map(({ uniqueId, result }) => {
          this.store.dispatch(setProcessingStatus({ isProcessing: false }));
          const hasAdjustedCalcResponse = result?.adjustedCalculationResponse != null;
          const hasOptimized = result?.calculationResponse?.runStatus?.some(
            rs => rs.calCurveInformation?.calSamples?.some(s => s.optimizedAssignment != null)
          );
          if (!hasOptimized && !hasAdjustedCalcResponse) {
            result.valueAssignment = "OriginalCalculate";
          } else if (hasAdjustedCalcResponse) {
            result.valueAssignment = "CalibrateAdjustedCalculate";
          } else {
            result.valueAssignment = "AdjustedCalculate";
          }
          const isAdjusted = (action.calculationRequest?.materials_info?.value_Assignment ?? [])
            .some(assignments => assignments.length > 0);
          const calAdjustmentMode = action.calculationRequest?.cal_Adjustment_Mode;
          const isFinal = calAdjustmentMode === 'Final';
          this.store.dispatch(setLastCalculationCache({
            calculationId: uniqueId,
            calculationType: isAdjusted ? 'adjusted' : 'unadjusted',
            // A Final run has no baseline, so the Adjust run's unadjusted id must not be saved with it.
            ...(isFinal && { clearUnadjusted: true })
          }));
          return runCalculationSuccess({
            calculationResponse: result,
            ...(calAdjustmentMode && { calAdjustmentMode }),
            ...(isFinal && { finalValueAssignment: action.calculationRequest.materials_info.value_Assignment })
          });
        }),
        catchError(error => {
          this.store.dispatch(setProcessingStatus({ isProcessing: false }));
          this.appUtilityService.displayAlertDialog({
            title: "Calculation Error",
            message: (error as Error)?.message || this.appUtilityService.errorMessages.serviceFailure,
            okText: 'OK',
            dismissText: '',
            okAction: () => { },
            dismissAction: () => { }
          } as CommonDialogData);

          return of(runCalculationError({ error: error }));
        })
        );
      })
    );
  });
  /** Effect to handle loading a Calibration lot when `loadCalLot` is dispatched */
  loadCalLotEffect = createEffect(() => {
    return this.actions$.pipe(
      ofType(loadCalLot),
      mergeMap(action => {
        const versionQuery = action.assignmentVersion === null || action.assignmentVersion === undefined
          ? ''
          : `?version=${encodeURIComponent(action.assignmentVersion)}`;
        return this.appUtilityService.get<CalLot>(
          this.appUtilityService.getAPI(`${apiPaths.fileOperations.importCalLot}${action.lotNumber}/${action.specRevision}${versionQuery}`),
        ).pipe(
          map(calLot => loadCalLotSuccess({ calLot: calLot, isRestore: action.isRestore })),
          catchError(error => of(loadCalLotError({ error })))
        );
      })
    );
  });
  /** Effect to handle loading a Quality Control lot when `loadQcLot` is dispatched */
  loadQcLotEffect = createEffect(() => {
    return this.actions$.pipe(
      ofType(loadQcLot),
      mergeMap(action => {
        const versionQuery = action.assignmentVersion === null || action.assignmentVersion === undefined
          ? ''
          : `?version=${encodeURIComponent(action.assignmentVersion)}`;
        return this.appUtilityService.get<QcLot>(
          this.appUtilityService.getAPI(`${apiPaths.fileOperations.importQcLot}${action.lotNumber}/${action.specRevision}${versionQuery}`),
        ).pipe(
          map(qcLot => loadQcLotSuccess({ qcLot: normalizeQcLot(qcLot), isRestore: action.isRestore })),
          catchError(error => of(loadQcLotError({ error })))
        );
      })
    );
  });
  /** Effect to handle saving a study when the `saveStudy` action is dispatched */
  saveStudyEffect = createEffect(() => {
    return this.actions$.pipe(
      ofType(saveStudy),
      concatLatestFrom(() => this.appState$),
      mergeMap(([action, appState]) => {
        const hasCalculationIds = Boolean(
          action.saveStudyRequest?.unadjusted_calculation_id
          || action.saveStudyRequest?.adjusted_calculation_id
        );
        const hasLoadedCalculationData = Boolean(
          appState.selectedStudy?.calculationResponse?.calculationResponse
          || appState.selectedStudy?.calculationAdjustedResponse?.calculationResponse
        );
        const hasCalculation = hasCalculationIds || hasLoadedCalculationData;
        const saveEndpoint = hasCalculation ? apiPaths.study.save : apiPaths.study.saveDraft;
        const saveRequest$ = hasCalculation
          ? this.appUtilityService.postWithPolling<SaveStudyResponse>(
              this.appUtilityService.getAPI(saveEndpoint),
              action.saveStudyRequest,
              'save'
            ).pipe(map(({ result }) => result))
          : this.appUtilityService.post<SaveStudyRequest, SaveStudyResponse>(
              this.appUtilityService.getAPI(saveEndpoint),
              action.saveStudyRequest
            );

        return saveRequest$.pipe(
          mergeMap(result => this.finalizeSave(result, hasCalculation, action.autoLinkToStudyName)),
          catchError(error => {
            this.store.dispatch(setProcessingStatus({ isProcessing: false }));
            const errorMessage = this.appUtilityService.getError(error).message;
            this.appUtilityService.displayAlertDialog({
              title: "Save Study Error",
              message: errorMessage,
              okText: 'OK',
              dismissText: '',
              okAction: () => { },
              dismissAction: () => { }
            } as CommonDialogData);

            return of(saveStudyError({ error: error }));
          })
        );
      })
    )
  });
  /**
   * Turns a save response into its success action, adding the Save-As link back to the source
   * study when the draft endpoint handled the save.
   *
   * A full save already carries the pair in `related_study_names`, but `studies/save_draft/`
   * ignores that field, so the linkage is made with a follow-up edit there. A failed edit does
   * not fail the save — the study is on disk either way, and the operator is told what is missing.
   */
  private finalizeSave(
    result: SaveStudyResponse,
    hasCalculation: boolean,
    autoLinkToStudyName?: string
  ): Observable<ReturnType<typeof saveStudySuccess>> {
    this.store.dispatch(setProcessingStatus({ isProcessing: false }));
    const skipped = result.skipped_related_studies ?? [];
    if (skipped.length) {
      this.appUtilityService.showSnackbar(
        `Saved, but these linked studies were not found and were skipped: ${skipped.join(', ')}`
      );
    }

    const successProps = {
      studyName: result.study_name,
      studyId: result.study_id,
      auditTrail: result.audit_trail
    };

    if (hasCalculation || !autoLinkToStudyName || !result.study_name) {
      return of(saveStudySuccess(successProps));
    }

    // The saved study is also the loaded one, so the edit answers with its refreshed audit trail.
    return this.studyLinksService.add(result.study_name, autoLinkToStudyName, result.study_name).pipe(
      map(response => saveStudySuccess(
        response.auditTrail ? { ...successProps, auditTrail: response.auditTrail } : successProps
      )),
      catchError(() => {
        this.appUtilityService.showSnackbar(
          `Saved, but this study could not be linked to ${autoLinkToStudyName}.`
        );
        return of(saveStudySuccess(successProps));
      })
    );
  }

  /** Effect to handle committing a study when the `commitStudy` action is dispatched */
  commitStudyEffect = createEffect(() => {

    return this.actions$.pipe(
      ofType(commitStudy),
      concatLatestFrom(() => this.appState$),
      mergeMap(([action, appState]) => this.appUtilityService.post<CommitRequest, SaveStudyResponse>(
        this.appUtilityService.getAPI(apiPaths.study.commitStudy),
        action.commitStudyRequest
      ).pipe(
        map(response => {
          const responseAny = response as unknown as {
            study_name?: string;
            run_name?: string;
            studyName?: string;
            audit_trail?: Array<{ name: string; date: string | null; action?: string | null; comment?: string | null }>;
            auditTrail?: Array<{ name: string; date: string | null; action?: string | null; comment?: string | null }>;
          };

          const studyName = responseAny.study_name
            ?? responseAny.run_name
            ?? responseAny.studyName
            ?? action.commitStudyRequest.run_name;

          const existingAuditTrail = appState.selectedStudy?.auditTrail ?? [];
          const commitNotes = (action.commitStudyRequest.notes ?? '').trim();
          const commitComment = commitNotes ? `Study committed: ${commitNotes}` : 'Study committed';

          const appendedAuditTrail = [
            ...existingAuditTrail,
            {
              name: appState.loggedInUser ?? '',
              date: new Date().toISOString(),
              comment: commitComment
            }
          ];

          const auditTrail = responseAny.audit_trail ?? responseAny.auditTrail ?? appendedAuditTrail;

          return saveStudySuccess({ studyName: studyName, auditTrail: auditTrail, isCommitted: true });
        }),
        catchError(error => {
          this.appUtilityService.displayAlertDialog({
            title: "Commit Study Error",
            message: this.appUtilityService.errorMessages.serviceFailure,
            okText: 'OK',
            dismissText: '',
            okAction: () => { },
            dismissAction: () => { }
          } as CommonDialogData);

          return of(saveStudyError({ error: error }));
        }))
      ))
  });

  /**
   * Marks the Studies screen's cached payloads stale once a save or a commit lands, so the
   * Logbook and Linked Studies tabs show the study as it now stands rather than as it was when
   * they first fetched. Both are dropped together: a save can rewrite a study's linkages as
   * well as its row.
   */
  invalidateStudyLogbookEffect = createEffect(() => {
    return this.actions$.pipe(
      ofType(saveStudySuccess),
      tap(() => {
        this.studyLinksService.invalidate();
        this.studyLogbookService.invalidate();
      })
    );
  }, { dispatch: false });

  /**
   * Ensure sample data is fetched only when needed and only once per calculation run.
   */
  ensureSampleData$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ensureSampleData),
      concatLatestFrom(() => this.appState$),
      mergeMap(([, state]) => {
        const reportContentItemIds = (state.selectedStudy?.reportContentItems?.reportContentItems ?? [])
          .map(item => item.reportContentItemId)
          .filter((id): id is string => !!id);
        if (!reportContentItemIds.length) {
          return EMPTY;
        }
        if (state.sampleDataFresh || state.sampleDataLoading) {
          return EMPTY;
        }
        return from([
          beginSampleDataLoad(),
          loadSampleData({ reportContentItemIds, failedOnly: false }),
          loadSampleData({ reportContentItemIds, failedOnly: true })
        ]);
      })
    );
  });

  /**
   * Hit pull_sample_data and stash the rows in NgRx.
   * The backend already knows which samples failed, so we simply forward the request parameters and cache the response.
   */
  fetchSampleDataEffect = createEffect(() => {
    return this.actions$.pipe(
      ofType(loadSampleData),
      concatLatestFrom(() => this.appState$),
      mergeMap(([action, state]) => {
        const runName = state.selectedStudy?.studyName?.trim();
        const hasRecentCalculation = !!state.lastCalculationId;
        const shouldUseRunName = !!runName && !!state.selectedStudy?.studyId && !state.selectedStudy?.isDraft && !hasRecentCalculation;
        const queryParams = new URLSearchParams({
          report_content_item_ids: JSON.stringify(action.reportContentItemIds),
          ...(action.failedOnly ? { failed_only: 'true' } : {}),
          ...(shouldUseRunName ? { run_name: runName } : {})
        });
        const url = `${this.appUtilityService.getAPI(apiPaths.study.pullSampleData)}?${queryParams.toString()}`;

        return this.appUtilityService.post<Record<string, never>, PullSampleDataResponse>(
          url,
          {}
        ).pipe(
          map(response => loadSampleDataSuccess({
            failedOnly: action.failedOnly,
            unadjustedSamples: response?.unadjustedSamples ?? [],
            adjustedSamples: response?.adjustedSamples ?? []
          })),
          catchError(error => of(loadSampleDataFailure({ failedOnly: action.failedOnly, error })))
        );
      })
    );
  });

  /**
   * Warns the operator when a study breaches the discard or instrument true-error threshold.
   * Both entry points share this one path, and reducers run before effects, so the freshly stored
   * calculation response is already readable here. Fires every time by design.
   */
  discardThresholdWarningEffect = createEffect(() => {
    return this.actions$.pipe(
      ofType(setStudy, runCalculationSuccess),
      concatLatestFrom(() => this.store.select(selectDiscardWarningSummary)),
      tap(([, summary]) => {
        if (summary) {
          this.appUtilityService.displayDiscardWarningDialog(summary);
        }
      })
    );
  }, { dispatch: false });

}

